import { useState } from "react";
import { Plus, Edit, Trash2, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useBlogPosts } from "@/hooks/useBlogPosts";

const EMPTY_POST = { title: "", excerpt: "", content: "", category: "", cover_image: "", author: "KF Prints" };

function BlogPostForm({ form, setForm, editing, onSubmit, onCancel }) {
  const inputCls = "rounded-none border-border py-4 font-sans text-sm";
  return (
    <form onSubmit={onSubmit} className="bg-white border border-border p-6 mb-8 space-y-4" data-testid="blog-form">
      <h2 className="text-lg font-serif text-foreground mb-2">{editing ? "Edit Post" : "New Post"}</h2>
      <div className="grid grid-cols-2 gap-4">
        <Input data-testid="blog-title" placeholder="Title *" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} />
        <Input data-testid="blog-category" placeholder="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls} />
      </div>
      <Input data-testid="blog-cover" placeholder="Cover Image URL" value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} className={inputCls} />
      <Input data-testid="blog-excerpt" placeholder="Excerpt *" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className={inputCls} />
      <Textarea data-testid="blog-content" placeholder="Content *" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className="rounded-none border-border font-sans text-sm min-h-[200px]" />
      <div className="flex gap-3">
        <Button type="submit" data-testid="blog-save-btn" className="bg-[#0F172A] text-white rounded-none px-6 py-4 text-xs uppercase tracking-[0.12em] font-sans hover:bg-[#0F172A]/90">
          <Plus size={14} className="mr-2" /> {editing ? "Update Post" : "Create Post"}
        </Button>
        {editing && (
          <Button type="button" variant="outline" onClick={onCancel} className="rounded-none px-6 py-4 text-xs uppercase tracking-[0.12em] font-sans">
            Cancel
          </Button>
        )}
      </div>
    </form>
  );
}

function BlogPostRow({ post, onEdit, onDelete, onTogglePublish }) {
  return (
    <div className="bg-white border border-border p-4 flex items-center justify-between">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h3 className="font-serif text-base text-foreground truncate">{post.title}</h3>
          {!post.published && <span className="text-[10px] font-sans uppercase tracking-wider text-destructive bg-destructive/10 px-2 py-0.5">Draft</span>}
        </div>
        <p className="text-xs font-sans text-muted-foreground mt-1">{post.category} &middot; {new Date(post.created_at).toLocaleDateString()}</p>
      </div>
      <div className="flex items-center gap-2 ml-4">
        <button onClick={() => onTogglePublish(post)} data-testid={`toggle-publish-${post.id}`} className="p-2 hover:bg-background rounded" title={post.published ? "Unpublish" : "Publish"}>
          {post.published ? <Eye size={16} className="text-green-600" /> : <EyeOff size={16} className="text-muted-foreground" />}
        </button>
        <button onClick={() => onEdit(post)} data-testid={`edit-post-${post.id}`} className="p-2 hover:bg-background rounded">
          <Edit size={16} className="text-muted-foreground" />
        </button>
        <button onClick={() => onDelete(post.id)} data-testid={`delete-post-${post.id}`} className="p-2 hover:bg-background rounded">
          <Trash2 size={16} className="text-destructive" />
        </button>
      </div>
    </div>
  );
}

export const BlogManagement = () => {
  const { posts, savePost, deletePost, togglePublish } = useBlogPosts();
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_POST);

  const resetForm = () => { setForm(EMPTY_POST); setEditing(null); };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.title || !form.excerpt || !form.content) {
      toast.error("Title, excerpt and content are required");
      return;
    }
    const saved = await savePost(form, editing);
    if (saved) resetForm();
  };

  const handleDelete = (id) => {
    if (window.confirm("Delete this post?")) deletePost(id);
  };

  const startEdit = (post) => {
    setEditing(post.id);
    setForm({
      title: post.title,
      excerpt: post.excerpt,
      content: post.content,
      category: post.category || "",
      cover_image: post.cover_image || "",
      author: post.author || "KF Prints",
    });
  };

  return (
    <div data-testid="admin-blog-management">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-serif text-foreground">Blog Posts</h1>
      </div>
      <BlogPostForm form={form} setForm={setForm} editing={editing} onSubmit={handleSave} onCancel={resetForm} />
      <div className="space-y-3" data-testid="blog-posts-list">
        {posts.map((post) => (
          <BlogPostRow key={post.id} post={post} onEdit={startEdit} onDelete={handleDelete} onTogglePublish={togglePublish} />
        ))}
        {posts.length === 0 && <p className="text-sm font-sans text-muted-foreground text-center py-12">No blog posts yet. Create your first one above.</p>}
      </div>
    </div>
  );
};
